import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'

import get from 'lodash/get'
import defaultTo from 'lodash/defaultTo'
import isNil from 'lodash/isNil'
import assignWith from 'lodash/assignWith'
import pick from 'lodash/pick'

import { ChildProps } from 'react-apollo'
import { InputProps } from './userDataMutation'

import { employeeCountOptions, companyTypeOptions, sexOptions, countryOptions } from 'data'

import PageLayout from 'components/layout/pageLayout'
import Head from 'components/layout/head'
import Button from 'components/ui/button'
import SubNavigation from 'components/layout/subNavigation'
import Input from 'components/ui/input'
import Select from 'components/ui/select'
import Radio from 'components/ui/radio'
import Accordion from 'components/layout/accordion'

interface SettingsUserDataProps extends ChildProps<InputProps, Response>, RouteComponentProps {
  authenticated: boolean
  data: any
}

interface IErrorObject {
  [k: string]: string | null
}

type SettingsUserDataState = {
  errors: IErrorObject | null
  success: boolean
  company: string | null
  sex: string | null
  employeeCount: number | null
  firstname: string | null
  lastname: string | null
  dealer: string | null
  companyType: string | null
  street: string | null
  phone: string | null
  zipCode: string | null
  email: string | null
  city: string | null
  fax: string | null
  country: string | null
  webAdress: string | null
}

class SettingsUserData extends Component<SettingsUserDataProps, SettingsUserDataState> {
  constructor(props: SettingsUserDataProps) {
    super(props)
    this.state = {
      errors: null,
      success: false,
      ...this.formFields,
    }
  }

  formFields = {
    company: null,
    sex: null,
    employeeCount: null,
    firstname: null,
    lastname: null,
    dealer: null,
    companyType: null,
    street: null,
    phone: null,
    zipCode: null,
    email: null,
    city: null,
    fax: null,
    country: null,
    webAdress: null,
  }

  getValue = (key: string) => {
    const { getUserData: userData } = this.props.data
    const stateValue = get(this.state, key)

    if (!isNil(stateValue)) {
      return stateValue
    }

    return get(userData, key)
  }

  getVariables = () => {
    const formFields = Object.keys(this.formFields)
    const variables = assignWith(this.props.data.getUserData, this.state, (objValue, srcValue) =>
      isNil(srcValue) ? objValue : srcValue,
    )

    // Filter out non form fields from state object
    return pick(variables, formFields)
  }

  isEmpty = (value: string) => isNil(value) || value === ''

  handleSave = () => {
    const { mutate } = this.props

    const errors = {
      company: this.isEmpty(this.getValue('company')) ? 'Error Message.' : null,
      sex: this.isEmpty(this.getValue('sex')) ? 'Error Message.' : null,
      employeeCount: this.isEmpty(this.getValue('employeeCount')) ? 'Error Message.' : null,
      firstname: this.isEmpty(this.getValue('firstname')) ? 'Error Message.' : null,
      lastname: this.isEmpty(this.getValue('lastname')) ? 'Error Message.' : null,
      companyType: this.isEmpty(this.getValue('companyType')) ? 'Error Message.' : null,
      street: this.isEmpty(this.getValue('street')) ? 'Error Message.' : null,
      phone: this.isEmpty(this.getValue('phone')) ? 'Error Message.' : null,
      zipCode: this.isEmpty(this.getValue('zipCode')) ? 'Error Message.' : null,
      email: this.isEmpty(this.getValue('email')) ? 'Error Message.' : null,
      city: this.isEmpty(this.getValue('city')) ? 'Error Message.' : null,
      country: this.isEmpty(this.getValue('country')) ? 'Error Message.' : null,
    }

    const hasErrors = Object.values(errors).find((error) => {
      if (error) return true
      return false
    })

    if (hasErrors) {
      this.setState({ errors, success: false })
    } else {
      if (mutate) {
        mutate({
          variables: { ...this.getVariables() },
        })
          .then((res) => {
            // User data change was sucessfull
            if (get(res, 'data.setUserData')) {
              this.setState({ errors: null, success: true })
            } else {
              this.setState({
                errors: {
                  api: 'Error Message',
                },
                success: false,
              })
            }
          })
          .catch((err) => {
            this.setState({
              errors: {
                api: 'Error Message',
              },
              success: false,
            })
          })
      }
    }
  }

  render() {
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle="Einstellungen">
        <Head
          headline="Einstellungen"
          subheadline={
            process.env.REACT_APP_ENV === 'rigips'
              ? 'Mengenermittlung und Systemsuche - HORST'
              : 'ABK calcKit – der Baukasten für die schnelle Kalkulation'
          }
          error={errors ? true : false}
          message={
            errors ? 'Pflichtfelder ausfüllen!' : success ? 'Änderungen gespeichert!' : undefined
          }
          actions={
            <Button primary onClick={this.handleSave}>
              Speichern
            </Button>
          }
        />
        <SubNavigation
          items={[
            {
              label: 'Nutzerdaten',
              link: `/settings/user-data`,
            },
            {
              link: `/settings/logo`,
              label: 'Firmenlogo',
            },
            {
              link: `/settings/company-details`,
              label: 'Firmendaten',
            },
            {
              link: `/settings/reset-password`,
              label: 'Passwort ändern',
            },
            {
              link: `/settings/price-overview`,
              label: 'Preisübersicht',
            },
            {
              link: `/settings/time-overview`,
              label: 'Zeitenübersicht',
            },
          ]}
        />

        <div className="px-48">
          <div className="flex">
            <div className="w-4/12 flex items-end">
              <Input
                name="company"
                label="Büro/Firma"
                required
                value={defaultTo(this.getValue('company'), '')}
                leftAlign
                error={errors && errors.company ? errors.company : undefined}
                onChange={(value) => this.setState({ company: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Radio
                label="Ansprechpartner"
                required
                value={defaultTo(this.getValue('sex'), sexOptions[0].value)}
                options={sexOptions}
                error={errors && errors.sex ? errors.sex : undefined}
                onChange={(value) => this.setState({ sex: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="dealer"
                label="Bei welchen Händler kaufen Sie Ihre Trockenbauprodukte?"
                value={defaultTo(this.getValue('dealer'), '')}
                leftAlign
                onChange={(value) => this.setState({ dealer: value ? String(value) : '' })}
              />
            </div>
          </div>
          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Select
                label="Anzahl Mitarbeiter"
                error={errors && errors.employeeCount ? errors.employeeCount : undefined}
                onChange={(option) =>
                  this.setState({ employeeCount: option.value ? Number(option.value) : null })
                }
                required
                value={defaultTo(
                  employeeCountOptions.find(
                    (option) => option.value === this.getValue('employeeCount'),
                  ),
                  employeeCountOptions[0],
                )}
                options={employeeCountOptions}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="firstname"
                label="Vorname"
                required
                value={defaultTo(this.getValue('firstname'), '')}
                leftAlign
                error={errors && errors.firstname ? errors.firstname : undefined}
                onChange={(value) => this.setState({ firstname: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>
          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Select
                label="Art der Firma"
                error={errors && errors.companyType ? errors.companyType : undefined}
                onChange={(option) =>
                  this.setState({ companyType: option.value ? String(option.value) : '' })
                }
                required
                value={defaultTo(
                  companyTypeOptions.find(
                    (option) => option.value === this.getValue('companyType'),
                  ),
                  companyTypeOptions[0],
                )}
                options={companyTypeOptions}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="lastname"
                label="Nachname"
                required
                value={defaultTo(this.getValue('lastname'), '')}
                leftAlign
                error={errors && errors.lastname ? errors.lastname : undefined}
                onChange={(value) => this.setState({ lastname: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="street"
                label="Straße"
                leftAlign
                value={defaultTo(this.getValue('street'), '')}
                error={errors && errors.street ? errors.street : undefined}
                onChange={(value) => this.setState({ street: value ? String(value) : '' })}
                required
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="phone"
                label="Telefon"
                required
                value={defaultTo(this.getValue('phone'), '')}
                leftAlign
                error={errors && errors.phone ? errors.phone : undefined}
                onChange={(value) => this.setState({ phone: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                type="number"
                name="zipCode"
                min={0}
                label="PLZ"
                leftAlign
                value={defaultTo(this.getValue('zipCode'), '')}
                error={errors && errors.zipCode ? errors.zipCode : undefined}
                onChange={(value) => this.setState({ zipCode: value ? String(value) : '' })}
                required
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="email"
                type="email"
                label="E-Mail"
                required
                value={defaultTo(this.getValue('email'), '')}
                leftAlign
                error={errors && errors.email ? errors.email : undefined}
                onChange={(value) => this.setState({ email: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="city"
                label="Ort"
                leftAlign
                value={defaultTo(this.getValue('city'), '')}
                error={errors && errors.city ? errors.city : undefined}
                onChange={(value) => this.setState({ city: value ? String(value) : '' })}
                required
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end">
              <Input
                name="fax"
                label="Fax"
                value={defaultTo(this.getValue('fax'), '')}
                leftAlign
                onChange={(value) => this.setState({ fax: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Select
                label="Land"
                options={countryOptions}
                value={defaultTo(
                  countryOptions.find((option) => option.value === this.getValue('country')),
                  countryOptions[0],
                )}
                required
                error={errors && errors.country ? errors.country : undefined}
                onChange={(option) => this.setState({ country: String(option.value) })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>

          <div className="flex mt-34.5 flex items-end">
            <div className="w-4/12">
              <Input
                name="webAdress"
                label="Website"
                leftAlign
                value={defaultTo(this.getValue('webAdress'), '')}
                onChange={(value) => this.setState({ webAdress: value ? String(value) : '' })}
              />
            </div>
            <div className="w-4/12 ml-30 flex items-end"></div>
            <div className="w-4/12 ml-30 flex items-end"></div>
          </div>
        </div>

        <div className="my-65">
          <Accordion preExpanded heading="Haftungsausschluss">
            <div className="px-48">
              <p className="font-400 text-black text-15 leading-22 tracking-0.1">
                Dieses Programm wurde sorgfältig programmiert und die Produktdaten sorgfältig
                zusammengestellt. Dennoch haftet Rigips nicht für Schäden infolge der Benutzung des
                Programms. Insbesondere ist jede Haftung für Schäden (z. B. eine fehlerhafte
                Planung) ausgeschlossen, die durch Verwendung von Berechnungsergebnissen oder von
                Daten oder Informationen aus dem Programm verursacht wurden. Der Haftungsausschluss
                gilt nicht für Schäden aus der Verletzung von Leben, Körper und Gesundheit sowie für
                Schäden, die auf einer vorsätzlichen oder grob fahrlässigen Pflichtverletzung von
                Rigips oder auf einer vorsätzlichen oder grob fahrlässigen Pflichtverletzung eines
                gesetzlichen Vertreters oder Erfüllungsgehilfen von Rigips beruhen.
              </p>
            </div>
          </Accordion>
        </div>
      </PageLayout>
    )
  }
}

export default SettingsUserData
